import axios from 'axios'

import type {
  CallbackResponse,
  CheckTokenResponse,
  DashboardParams,
  DashboardResponse,
  LoginResponse,
  TestLoginResponse,
  TestUserResponse,
  WithRedirectResponse
} from './model/authType'

import api from '@/api/url'
import { logger } from '@/utils/logger'

export const getLoginApi = async (provider: string): Promise<LoginResponse> => {
  logger.auth(`로그인 API 호출 시작 - Provider: ${provider}`)
  try {
    const response = await axios.get(api.auth.LOGIN_URL(provider))
    logger.success(`로그인 API 호출 성공 - Provider: ${provider}`)
    return response.data
  } catch (error) {
    logger.error(`로그인 API 호출 실패 - Provider: ${provider}`, error)
    throw error
  }
}

export const getCallbackApi = async (provider: string, code: string, user: string): Promise<CallbackResponse> => {
  logger.auth(`콜백 API 호출 시작 - Provider: ${provider}, User: ${user}`)
  try {
    const response = await axios.get(api.auth.HANDLE_CALLBACK(provider), {
      params: { code, redirect_url: window.location.origin + window.location.pathname, user }
    })
    logger.success(`콜백 API 호출 성공 - Provider: ${provider}`)
    return response.data
  } catch (error) {
    logger.error(`콜백 API 호출 실패 - Provider: ${provider}`, error)
    throw error
  }
}

// 네이티브 토큰을 사용한 인증 (WebView용)
export const getNativeTokenCallbackApi = async (
  provider: string,
  tokenData: { idToken: string; accessToken: string }
): Promise<CallbackResponse> => {
  logger.auth(`네이티브 토큰 콜백 API 호출 - Provider: ${provider}`)
  logger.token('토큰 데이터 확인', tokenData)

  const url = api.auth.HANDLE_CALLBACK(provider)
  const requestData = {
    idToken: tokenData.idToken,
    accessToken: tokenData.accessToken,
    authType: 'native'
  }

  logger.api(`요청 URL: ${url}`)
  logger.api('전송할 데이터', requestData)

  try {
    logger.api('HTTP 요청 시작...')
    const response = await axios.post(url, requestData)
    logger.success('백엔드 응답 성공!')
    logger.debug('응답 상태:', response.status)
    logger.debug('응답 헤더:', response.headers)
    logger.auth('응답 데이터:', response.data)

    return response.data
  } catch (error: any) {
    logger.error('백엔드 API 호출 실패!')
    logger.error('에러 타입:', typeof error)
    logger.error('에러 이름:', error.name)
    logger.error('에러 메시지:', error.message)

    if (error.response) {
      // 서버가 응답했지만 2xx 범위가 아닌 상태 코드
      logger.error('HTTP 응답 에러!')
      logger.error('응답 상태:', error.response.status)
      logger.error('응답 상태 텍스트:', error.response.statusText)
      logger.error('응답 헤더:', error.response.headers)
      logger.error('응답 데이터:', error.response.data)
    } else if (error.request) {
      // 요청이 만들어졌지만 응답을 받지 못함
      logger.error('네트워크 에러 - 응답 없음!')
      logger.debug('요청 객체:', error.request)
    } else {
      // 요청을 설정하는 중에 오류가 발생
      logger.error('요청 설정 에러!')
      logger.error('에러 메시지:', error.message)
    }

    logger.debug('요청 설정:', error.config)
    throw error
  }
}

// 리프레시 토큰을 헤더에 포함하여 새로운 토큰 요청 (더 이상 사용하지 않음 - Axios 인터셉터가 처리)
// export const getRefreshTokenApi = async (): Promise<RefreshTokenResponse> => {
//   // 새로운 axios 인스턴스 생성 (인터셉터 우회)
//   const axiosInstance = axios.create()
//   const response = await axiosInstance.post(api.auth.REFRESH_TOKEN(), {}, {
//     headers: { Authorization: `Bearer ${refreshToken}` }
//   })
//   return response.data
// }

export const getDashboardApi = async (params: DashboardParams): Promise<DashboardResponse> => {
  logger.api('대시보드 API 호출', params)
  try {
    const response = await axios.get(api.auth.DASHBOARD(), { params })
    logger.success('대시보드 API 호출 성공')
    return response.data
  } catch (error) {
    logger.error('대시보드 API 호출 실패', error)
    throw error
  }
}

export const getWithRedirectApi = async (provider: string, redirect_url: string): Promise<WithRedirectResponse> => {
  logger.auth(`리다이렉트 URL로 로그인 - Provider: ${provider}`)
  try {
    const response = await axios.get(api.auth.WITH_REDIRECT(provider), { params: { redirect_url: redirect_url } })
    logger.success(`리다이렉트 로그인 성공 - Provider: ${provider}`)
    return response.data
  } catch (error) {
    logger.error(`리다이렉트 로그인 실패 - Provider: ${provider}`, error)
    throw error
  }
}

export const getCheckTokenApi = async (): Promise<CheckTokenResponse> => {
  logger.token('토큰 검증 API 호출')
  try {
    const response = await axios.get(api.auth.CHECK_TOKEN())
    logger.token('토큰 검증 성공')
    return response.data
  } catch (error) {
    logger.error('토큰 검증 실패', error)
    throw error
  }
}

export const getTestUsersApi = async (): Promise<TestUserResponse> => {
  logger.debug('테스트 사용자 목록 조회')
  try {
    const response = await axios.get(api.auth.TEST_USERS())
    logger.debug('테스트 사용자 목록 조회 성공')
    return response.data
  } catch (error) {
    logger.error('테스트 사용자 목록 조회 실패', error)
    throw error
  }
}

export const getTestLoginApi = async (user_id: number): Promise<TestLoginResponse> => {
  logger.auth(`테스트 로그인 시작 - User ID: ${user_id}`)
  try {
    const response = await axios.post(api.auth.TEST_LOGIN(), { user_id })
    logger.success(`테스트 로그인 성공 - User ID: ${user_id}`)
    return response.data
  } catch (error) {
    logger.error(`테스트 로그인 실패 - User ID: ${user_id}`, error)
    throw error
  }
}

// test 계정 중 is_new 가 true 인 경우 호출하는 api (zep memory 추가)
export const addTestMemory = async (): Promise<string> => {
  logger.debug('테스트 메모리 추가 시작')
  try {
    const response = await axios.post(api.auth.ADD_TEST_MEMORY())
    logger.success('테스트 메모리 추가 성공')
    return response.data
  } catch (error) {
    logger.error('테스트 메모리 추가 실패', error)
    throw error
  }
}
